import { SEO, pageSEO } from '../components/shared/SEO';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  CreditCard, Workflow, CreditCardIcon, Calendar,
  ArrowRight, CheckCircle, DollarSign, RefreshCcw
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { PageHeader } from '../components/shared/SectionHeader';
import { AnimatedSection, StaggerContainer, StaggerItem } from '../components/shared/AnimatedSection';

const capabilities = [
  {
    icon: Workflow,
    title: 'Custom Billing Flows',
    description: 'Design billing workflows tailored to your business model, from direct bill to agency bill scenarios.',
    features: [
      'Direct bill configuration',
      'Agency bill management',
      'Consolidated billing',
      'Split billing options',
    ],
  },
  {
    icon: CreditCardIcon,
    title: 'Payment Gateway Integrations',
    description: 'Connect with leading payment processors for seamless, secure payment collection across channels.',
    features: [
      'Multi-gateway support',
      'ACH & credit card',
      'Real-time processing',
      'PCI compliance',
    ],
  },
  {
    icon: Calendar,
    title: 'Installments & Subscriptions',
    description: 'Flexible payment plans with automated installment scheduling and recurring billing support.',
    features: [
      'Payment plan configuration',
      'Automated scheduling',
      'Grace period management',
      'Late fee automation',
    ],
  },
  {
    icon: RefreshCcw,
    title: 'Billing Automation',
    description: 'Automate billing processes from invoice generation to collections with intelligent workflows.',
    features: [
      'Auto-invoicing',
      'Dunning management',
      'Write-off automation',
      'Commission processing',
    ],
  },
];

const billingFlow = [
  { icon: DollarSign, title: 'Invoice Generation', desc: 'Automated invoice creation' },
  { icon: CreditCard, title: 'Payment Collection', desc: 'Multi-channel payments' },
  { icon: RefreshCcw, title: 'Reconciliation', desc: 'Automated matching' },
  { icon: Calendar, title: 'Dunning & Collections', desc: 'Smart follow-ups' },
];

const BillingCenter = () => {
  return (
    <div data-testid="billingcenter-page">
      <SEO {...pageSEO.billingCenter} />
      <PageHeader
        label="Core Platform"
        title="Billing & Payments Modernisation"
        description="Modern billing solutions with seamless payment integrations, flexible installment plans, and comprehensive automation."
      />

      {/* Overview Section */}
      <section className="py-20 lg:py-32 bg-white dark:bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center">
                  <CreditCard className="w-6 h-6 text-sky-600" />
                </div>
                <span className="text-sky-600 font-semibold">BillingCenter</span>
              </div>
              <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-6 tracking-tight">
                Streamline Your Billing Operations
              </h2>
              <p className="text-lg text-slate-600 mb-6 leading-relaxed">
                Efficient billing operations are critical to cash flow and customer satisfaction. 
                Our BillingCenter expertise helps you optimize the entire billing lifecycle 
                from invoicing to collections.
              </p>
              <p className="text-lg text-slate-600 mb-8 leading-relaxed">
                We implement flexible billing solutions that support your business model 
                while providing customers with convenient payment options and transparent 
                account management.
              </p>
              <Link to="/contact">
                <Button 
                  size="lg"
                  className="bg-sky-600 hover:bg-sky-700 text-white"
                >
                  Discuss Your Project
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </AnimatedSection>
            
            <AnimatedSection direction="right">
              {/* Billing Flow Diagram */}
              <div className="relative bg-gradient-to-br from-sky-50 to-slate-50 rounded-2xl p-8">
                <h4 className="text-lg font-bold text-slate-900 mb-8 text-center">
                  Automated Billing Flow
                </h4>
                <div className="grid grid-cols-2 gap-6">
                  {billingFlow.map((item, index) => (
                    <motion.div
                      key={index}
                      initial={{ opacity: 0, scale: 0.9 }}
                      whileInView={{ opacity: 1, scale: 1 }}
                      viewport={{ once: true }}
                      transition={{ delay: index * 0.1 }}
                      className="bg-white rounded-xl p-6 shadow-sm text-center"
                    >
                      <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center mx-auto mb-4">
                        <item.icon className="w-6 h-6 text-sky-600" />
                      </div>
                      <h5 className="font-semibold text-slate-900 mb-1">{item.title}</h5>
                      <p className="text-sm text-slate-500">{item.desc}</p>
                    </motion.div>
                  ))}
                </div>
                {/* Connection arrows */}
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }}>
                  <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="7" refX="9" refY="3.5" orient="auto">
                      <polygon points="0 0, 10 3.5, 0 7" fill="#0EA5E9" fillOpacity="0.5" />
                    </marker>
                  </defs>
                </svg>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="py-12 bg-slate-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              { value: '95%', label: 'Collection Rate' },
              { value: '60%', label: 'Faster Processing' },
              { value: '80%', label: 'Less Manual Work' },
              { value: '30%', label: 'Cost Savings' },
            ].map((stat, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="text-center"
              >
                <div className="text-3xl lg:text-4xl font-bold text-white mb-1">
                  {stat.value}
                </div>
                <div className="text-sm text-slate-400">
                  {stat.label}
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities Grid */}
      <section className="py-20 lg:py-32 bg-slate-50 dark:bg-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <AnimatedSection className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 bg-sky-100 text-sky-700 text-sm font-semibold rounded-full mb-4">
              Capabilities
            </span>
            <h2 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-4 tracking-tight">
              Our BillingCenter Expertise
            </h2>
            <p className="text-base lg:text-lg text-slate-600 max-w-2xl mx-auto">
              Comprehensive billing solutions that improve cash flow and customer experience.
            </p>
          </AnimatedSection>

          <StaggerContainer className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {capabilities.map((capability, index) => (
              <StaggerItem key={index}>
                <div className="bg-white rounded-2xl p-8 shadow-sm border border-slate-100 hover:shadow-lg transition-shadow duration-300 h-full">
                  <div className="w-14 h-14 rounded-xl bg-sky-50 flex items-center justify-center mb-6">
                    <capability.icon className="w-7 h-7 text-sky-600" />
                  </div>
                  <h3 className="text-xl font-bold text-slate-900 mb-3">
                    {capability.title}
                  </h3>
                  <p className="text-slate-600 mb-6 leading-relaxed">
                    {capability.description}
                  </p>
                  <ul className="space-y-2">
                    {capability.features.map((feature, idx) => (
                      <li key={idx} className="flex items-center gap-2 text-sm text-slate-500">
                        <CheckCircle className="w-4 h-4 text-sky-500 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
              </StaggerItem>
            ))}
          </StaggerContainer>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 lg:py-32 bg-gradient-to-br from-slate-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <AnimatedSection>
            <h2 className="text-3xl sm:text-4xl font-bold mb-6 tracking-tight">
              Ready to Modernize Your Billing Operations?
            </h2>
            <p className="text-lg text-slate-300 max-w-2xl mx-auto mb-8">
              Our BillingCenter experts can help you streamline collections and 
              improve customer payment experiences.
            </p>
            <Link to="/contact">
              <Button 
                size="lg"
                className="bg-sky-500 hover:bg-sky-600 text-white h-14 px-8"
              >
                Request a Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </div>
  );
};

export default BillingCenter;
